import { ellipsis, emDash, InputRule, smartQuotes, undoInputRule } from "prosemirror-inputrules";
import { NodeType, Node as ProseNode } from "prosemirror-model";
import { EditorState, Plugin, TextSelection, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { Extension } from "~/lib/prose/extension";

const MAX_MATCH = 500;

type Handler = (state: EditorState, match: RegExpExecArray, from: number, to: number) => Transaction | null;

function runInputRule({
	view,
	from,
	to,
	text,
	rules,
	plugin,
	matchExistingText,
}: {
	view: EditorView;
	from: number;
	to: number;
	text: string;
	rules: InputRule[];
	plugin: Plugin;
	matchExistingText?: boolean;
}) {
	if (view.composing) return false;
	let state = view.state,
		$from = state.doc.resolve(from);
	// In addition code nodes, we also don't apply input rules within our code mark.
	if ($from.parent.type.spec.code || $from.marks().some(m => m.type.name === "code")) return false;
	let textBeforeChange = $from.parent.textBetween(
		Math.max(0, $from.parentOffset - MAX_MATCH),
		$from.parentOffset,
		undefined,
		"\ufffc",
	);
	const textAfterChange = textBeforeChange + text;
	for (let i = 0; i < rules.length; i++) {
		// The typings for prosemirror-inputrules don't include the attributes on
		// InputRule. I guess they're meant to be private /shrug
		// @ts-ignore
		const {
			match: regex,
			handler,
		}: {
			match: RegExp;
			handler: Handler;
		} = rules[i];

		let match = regex.exec(textAfterChange);
		let tr = null;
		// Differently from prosemirror-inputrules, we only consider a match if the
		// entered text is what caused the match. This way if you undo an inputrule
		// that would still match after you keep typing, it doesn't replace it
		// again.
		if ((matchExistingText || !textBeforeChange.match(regex)) && match) {
			tr = handler(state, match, from - (match[0].length - text.length), to);
		}
		if (!tr) continue;
		view.dispatch(tr.setMeta(plugin, { transform: tr, from, to, text }));
		return true;
	}
	return false;
}

/**
 * A slightly-modified implementation of `inputRules` from
 * prosemirror-inputrules[1].
 *
 * [1]: https://prosemirror.net/docs/ref/#inputrules
 *
 * @returns a prosemirror plugin that performs some action when an entered
 * sequence of text matches a regular expression. See the InputRule class.
 */
export function inputRules({ rules }: { rules: InputRule[] }) {
	let plugin: Plugin;
	return (plugin = new Plugin({
		// @ts-ignore
		isInputRules: true,

		// The state is used to implement undo functionality. The state is
		// maintained until the document changes, which means that undoing an
		// inputrule is only possible until the document has changed.
		state: {
			init() {
				return null;
			},
			apply(tr, prev) {
				const stored = tr.getMeta(plugin);
				if (stored) return stored;
				if (tr.docChanged) {
					// If the document is changed and it's not a remote change or should
					// be ignored, the state is reset.
					if (tr.getMeta("remoteChange") || tr.getMeta("inputRulesIgnore")) return prev;
					else return null;
				}
				return prev;
			},
		},

		props: {
			handleTextInput(view, from, to, text) {
				return runInputRule({ view, from, to, text, rules, plugin });
			},

			handleDOMEvents: {
				paste: (_, event) => {
					return event.target instanceof HTMLElement && event.target.tagName === "INPUT";
				},

				compositionend: view => {
					setTimeout(() => {
						const { selection } = view.state;
						if (selection instanceof TextSelection && selection.$cursor) {
							// In the case of composition[1], there is no change in text when
							// we call `runInputRule`, so we pass the `matchExistingText`
							// property to disregard the `text` property.
							//
							// [1]: https://developer.mozilla.org/en-US/docs/Web/API/Element/compositionend_event
							runInputRule({
								view,
								from: selection.$cursor.pos,
								to: selection.$cursor.pos,
								text: "",
								rules,
								plugin,
								matchExistingText: true,
							});
						}
					});
					return false;
				},
			},
		},
	}));
}

const rightArrow = new InputRule(/->$/, "→"),
	leftArrow = new InputRule(/<-$/, "←");

export default class InputRules extends Extension {
	name = "input rules";
	inputRules = () => [emDash, ellipsis, ...smartQuotes, rightArrow, leftArrow];

	keys() {
		return {
			// Note that we are using the same keyboard shortcut for undoing
			// inputrules as normal undo. This is called before the default undo
			// handler, and will return `false` if it has not handled the event,
			// delegating to the default handler.
			"Mod-z": undoInputRule,
		};
	}
}

type Attrs = { [key: string]: unknown };

/**
 * Make an inputrules handler that changes the type of the current block.
 *
 * @param shortcut - This string is injected into a regular expression, make
 * sure that anything that needs to be escaped is. The regex will match the
 * start of the text, then the given string, then whitespace.
 */
export function blockTypeShortcut(
	shortcut: string,
	type: NodeType,
	attrs?: Attrs | ((state: EditorState, node: ProseNode) => Attrs),
	{
		predicate,
		trailingWhitespace = true,
	}: { predicate?: (node: ProseNode) => boolean; trailingWhitespace?: boolean } = {},
): InputRule {
	let regex = new RegExp(`^${shortcut}\\s`);
	if (!trailingWhitespace) {
		regex = new RegExp(`^${shortcut}`);
	}

	return new InputRule(regex, function changeBlockType(state, _match, start, end) {
		const pos = state.doc.resolve(start);
		const node = pos.node(1);
		if (predicate && !predicate(node)) return null;

		let { tr } = state;
		// If we are moving from a multiple assignee action item
		// We need to remove the assignees
		const nodeAttrs = { ...node.attrs };
		if (
			type.name !== "todo" &&
			node.type.name === "todo" &&
			node.attrs &&
			node.attrs.assignees &&
			node.attrs.assignees.length > 1
		) {
			nodeAttrs["assignees"] = [];
		}
		tr = tr.delete(start, end);
		tr = tr.setNodeMarkup(start - 1, type, {
			...nodeAttrs,
			...(attrs instanceof Function ? attrs(state, node) : attrs),
		});

		return tr;
	});
}
